import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useEmbeddedHostSync } from '@/composables/useEmbeddedHostSync';

/**
 * Positions popover modals correctly when the app runs inside the host iframe.
 *
 * Business rule: the host sizes the iframe to its full content height, so the
 * library modal's `position: fixed` overlay anchors to the whole (tall) iframe
 * document rather than the user's visible area — the modal lands far down and
 * the user has to scroll up to find it. Using the host position-sync data we
 * expose the currently-visible band (top + height in iframe-document
 * coordinates) as CSS variables, and `PopoverRoot` re-anchors the overlay to
 * that band so the modal always opens where the user is looking.
 */
export const useEmbeddedModalPosition = () => {
  const { isEmbedded, iframeTop, viewportHeight } = useEmbeddedHostSync();

  // Why: the host resizes the iframe from our height reports without always sending a new position
  // sync, so the iframe's own height has to be tracked here to keep the band inside it.
  const frameHeight = ref(window.innerHeight);
  const onResize = () => {
    frameHeight.value = window.innerHeight;
  };

  onMounted(() => {
    if (isEmbedded) {
      window.addEventListener('resize', onResize);
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize);
  });

  const containerClasses = computed(() => {
    return isEmbedded ? ['embedded-modal-wrapper'] : [];
  });

  const containerStyle = computed(() => {
    if (!isEmbedded) {
      return {};
    }

    // The band is where the host's visible area and the iframe overlap. iframeTop is negative once
    // the host is scrolled down and positive while the iframe starts below the visible top.
    const visibleTop = Math.max(0, -iframeTop.value);
    // Edge case: before the first host sync arrives viewportHeight is 0 — fall
    // back to the iframe's own height so the band is never collapsed.
    const hostViewport =
      viewportHeight.value > 0 ? viewportHeight.value : frameHeight.value;
    // Edge case: a document shorter than the host viewport, or an iframe starting below the visible
    // top, would otherwise get a band reaching past the iframe's bottom, where it is cut off.
    const visibleBottom = Math.min(
      frameHeight.value,
      -iframeTop.value + hostViewport
    );
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);

    return {
      '--embedded-visible-top': `${visibleTop}px`,
      '--embedded-visible-height': `${visibleHeight}px`
    };
  });

  return { isEmbedded, containerClasses, containerStyle };
};
